// Pure data model behind map overlay layers: which items a layer renders and
// what value or segments each item carries. Shared by the Leaflet marker
// renderer (mapLayerMarkers) and the canvas exporter (mapPaint) so an exported
// image aggregates exactly like the live map.
import { aggregateValues, donutParts } from '../prepare';

// Positions for one map layer: one item per result row when groupKey is
// null (point layers), or one grouped item per distinct groupKey value with
// the group's centroid lat/lon (donuts, labels). Each item carries the rows
// that feed it (members) and a display label.
export function layerItems(points, groupKey) {
  if (!groupKey)
    return (points || []).map((p) => ({
      lat: p.lat,
      lon: p.lon,
      members: [p.row],
      label: p.label
    }));
  const groups = new Map();
  for (const p of points || []) {
    const key = String(p.row[groupKey] ?? '—');
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(p);
  }
  return Array.from(groups.entries()).map(([key, ps]) => ({
    lat: mean(ps.map((p) => p.lat)),
    lon: mean(ps.map((p) => p.lon)),
    members: ps.map((p) => p.row),
    label: key
  }));
}

export function mean(values) {
  return values.reduce((a, b) => a + b, 0) / values.length;
}

// A layer item's value: the configured aggregate of valueField, or the number
// of member rows when the layer has no value field.
export function valueFor(item, layer) {
  const { valueField, agg } = layer.options;
  if (!valueField) return item.members.length;
  return aggregateValues(item.members, valueField, agg || 'mean');
}

// Values scaled to 0..1 against the layer's largest value; non-numeric and
// missing values become zero-height markers.
export function fractionsOf(values) {
  const finite = values.filter((v) => Number.isFinite(v));
  const max = finite.length > 0 ? Math.max(...finite) : 1;
  return values.map((value) =>
    value == null || !Number.isFinite(value) || max <= 0 ? 0 : Math.max(0, value / max)
  );
}

// Donut columns: the configured ones, or (legacy configs) every distribution
// column of the group the old `field` option pointed at.
export function resolveDonutColumns(layer, items) {
  if (layer.options.columns && layer.options.columns.length > 0) return layer.options.columns;
  if (!layer.options.field) return [];
  const prefix = `distribution__${layer.options.field}__`;
  const seen = new Set();
  return items
    .flatMap((it) => Object.keys(it.members[0] || {}))
    .filter((k) => k.startsWith(prefix) && !seen.has(k) && seen.add(k));
}

// Donut segments of an item, one per selected column of its first member row.
// `labels` optionally maps a column key to a custom segment label.
export function itemDonutParts(item, columns, labels) {
  return donutParts(item.members, columns, null, null, labels);
}
